import React, { useState, useEffect, useRef } from 'react';
import { contact } from '../mock';
import { Phone, MessageCircle, Mail, User as UserIcon, Send, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const ref = useRef(null);
  const [form, setForm] = useState({ name: '', phone: '', message: '' });

  useEffect(() => {
    const els = ref.current?.querySelectorAll('.reveal');
    if (!els) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.15 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({
        title: 'Please fill in required fields',
        description: 'We need your name and phone number to reach out.',
      });
      return;
    }
    // Persist locally as a frontend-only mock
    const reqs = JSON.parse(localStorage.getItem('nc_requests') || '[]');
    reqs.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('nc_requests', JSON.stringify(reqs));
    toast({
      title: 'Request received',
      description: 'Thank you for reaching out. Santhosha will contact you shortly.',
    });
    setForm({ name: '', phone: '', message: '' });
  };

  return (
    <section id="contact" ref={ref} className="py-20 md:py-28 bg-[#f8f3e6] relative">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div className="reveal">
          <p className="font-script text-2xl text-[#7a8a4c] mb-2">Reach out</p>
          <h2 className="font-serif text-3xl md:text-5xl text-[#2c3f25] tracking-tight leading-[1.1]">
            Let’s take the first step — together.
          </h2>
          <p className="mt-5 text-[#3d4a36] text-base md:text-lg max-w-md leading-relaxed">
            Drop your details and I’ll get in touch. Or reach me directly on WhatsApp.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center gap-4 group"
            >
              <div className="w-14 h-14 rounded-full bg-[#3a5232] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
                <Phone className="text-[#f0e9d2]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#7a8a4c] tracking-wide uppercase">Contact / Call</p>
                <p className="font-serif text-2xl md:text-3xl text-[#2c3f25] tracking-wide">{contact.phone}</p>
              </div>
            </a>
            <a
              href={`https://wa.me/91${contact.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 group"
            >
              <div className="w-14 h-14 rounded-full bg-[#3a5232] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
                <MessageCircle className="text-[#f0e9d2]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#7a8a4c] tracking-wide uppercase">WhatsApp</p>
                <p className="font-serif text-2xl md:text-3xl text-[#2c3f25] tracking-wide">{contact.whatsapp}</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#3a5232] flex items-center justify-center shadow-md">
                <UserIcon className="text-[#f0e9d2]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#7a8a4c] tracking-wide uppercase">Sessions by</p>
                <p className="font-serif text-2xl md:text-3xl text-[#2c3f25] tracking-wide">{contact.name}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 pt-2">
              <div className="w-14 h-14 rounded-full bg-[#3a5232] flex items-center justify-center shadow-md shrink-0">
                <MapPin className="text-[#f0e9d2]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#7a8a4c] tracking-wide uppercase">Visit us</p>
                <p className="font-serif text-lg md:text-xl text-[#2c3f25] leading-snug mt-1">
                  {contact.address.line1},<br />
                  {contact.address.line2},<br />
                  {contact.address.city}
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="reveal bg-[#f0e9d2] rounded-[28px] p-7 md:p-10 shadow-sm border border-[#e5dcc0]"
        >
          <h3 className="font-serif text-2xl md:text-3xl text-[#2c3f25]">Request a session</h3>
          <p className="text-[#5a6a4a] text-sm mt-2">It’s completely confidential. We’ll reach out within a day.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-[#5a6a4a]">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="mt-1 bg-[#f8f3e6] border-[#d8cfb1] focus-visible:ring-[#3a5232]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-[#5a6a4a]">Phone</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone or WhatsApp"
                className="mt-1 bg-[#f8f3e6] border-[#d8cfb1] focus-visible:ring-[#3a5232]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-[#5a6a4a]">How can I help? (optional)</label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Share as much or as little as you like"
                rows={4}
                className="mt-1 bg-[#f8f3e6] border-[#d8cfb1] focus-visible:ring-[#3a5232]"
              />
            </div>
            <Button
              type="submit"
              className="btn-primary w-full rounded-full py-6 text-sm font-medium"
            >
              <Send size={16} className="mr-2" />
              Send request
            </Button>
            <p className="text-[11px] text-[#7a8a4c] text-center mt-2">
              You can also email us at <a href={`mailto:${contact.email}`} className="underline">{contact.email}</a>
            </p>
          </div>
        </form>
      </div>

      {/* Map embed */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 mt-16 reveal">
        <div className="flex items-center gap-3 mb-5">
          <MapPin size={20} className="text-[#3a5232]" />
          <h3 className="font-serif text-2xl md:text-3xl text-[#2c3f25]">Find us in Nagasandra, Bangalore</h3>
        </div>
        <div className="rounded-[28px] overflow-hidden shadow-lg border border-[#e5dcc0] bg-[#f0e9d2]">
          <iframe
            title="Natural Care location - Nagasandra, Bangalore"
            src="https://www.google.com/maps?q=Jodi+Bhavi+Road,+Nagasandra,+Bangalore,+Karnataka+560073&hl=en&z=15&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, display: 'block' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <p className="text-sm text-[#5a6a4a]">
            {contact.address.full}
          </p>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Jodi+Bhavi+Road+Nagasandra+Bangalore+560073"
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-[#3a5232] hover:text-[#7a8a4c] inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
          >
            <MapPin size={14} />
            Open in Google Maps
          </a>
        </div>
      </div>
    </section>
  );
};

export default Contact;
